import { z } from 'zod'
import { ReplyStatus, SuggestionOutcome, type ConversationKind, type Platform } from '../enums'
import { Id, Instant } from './primitives'

export const REPLY_TEXT_MAX_CHARS = 8000
export const MESSENGER_DM_MAX_CHARS = 2000
export const INSTAGRAM_DM_MAX_BYTES = 1000

export const REPLY_TEXT_UNITS = ['chars', 'bytes'] as const
export type ReplyTextUnit = (typeof REPLY_TEXT_UNITS)[number]
export type ReplyTextLimit = { max: number; unit: ReplyTextUnit }

export const COMMENT_TEXT_LIMIT: ReplyTextLimit = { max: REPLY_TEXT_MAX_CHARS, unit: 'chars' }
export const DM_TEXT_LIMITS: Record<Platform, ReplyTextLimit> = {
  facebook: { max: MESSENGER_DM_MAX_CHARS, unit: 'chars' },
  instagram: { max: INSTAGRAM_DM_MAX_BYTES, unit: 'bytes' },
}

export const replyTextLimit = (kind: ConversationKind, platform: Platform): ReplyTextLimit =>
  kind === 'dm' ? DM_TEXT_LIMITS[platform] : COMMENT_TEXT_LIMIT

const utf8 = new TextEncoder()

export const measureReplyText = (text: string, unit: ReplyTextUnit): number => {
  const trimmed = text.trim()
  return unit === 'bytes' ? utf8.encode(trimmed).length : trimmed.length
}

export const REPLY_TEXT_UNIT_LABELS: Record<ReplyTextUnit, string> = {
  chars: '個字',
  bytes: '位元組',
}
export const REPLY_TEXT_BYTES_HINT = '中文字每字約佔 3 位元組'

export const replyTooLongMessage = (limit: ReplyTextLimit): string => {
  const hint = limit.unit === 'bytes' ? `（${REPLY_TEXT_BYTES_HINT}）` : ''
  return `回覆最多 ${limit.max} ${REPLY_TEXT_UNIT_LABELS[limit.unit]}${hint}，請縮短後再發送。`
}
export const CUSTOMER_WROTE_AGAIN_MESSAGE = '客人傳了新訊息，你的回覆沒有送出。請先看過新訊息，再決定怎樣回覆。'
export const UNKNOWN_CUSTOMER_MESSAGE = '這則回覆所指的客人訊息不屬於這個項目，請重新整理後再試'

export const META_FAILURE_REASONS = [
  'post_deleted',
  'channel_disconnected',
  'permission_denied',
  'policy_rejected',
  'rate_limited',
  'window_closed',
  'unreachable',
  'unavailable',
  'unknown',
] as const
export const MetaFailureReason = z.enum(META_FAILURE_REASONS)
export type MetaFailureReason = z.infer<typeof MetaFailureReason>

export const MetaFailure = z.object({ reason: MetaFailureReason.catch('unknown'), message: z.string() })
export type MetaFailure = z.infer<typeof MetaFailure>

export const LIVE_REPLY_STATUSES = ['publishing', 'published'] as const satisfies readonly ReplyStatus[]

export const replyIsLive = (status: ReplyStatus): boolean => LIVE_REPLY_STATUSES.some((live) => live === status)

export const Reply = z.object({
  id: Id,
  status: ReplyStatus,
  text: z.string(),
  suggestionId: Id.nullable(),
  inReplyToMessageId: Id.nullable(),
  outcome: SuggestionOutcome.nullable(),
  externalId: z.string().nullable(),
  metaFailure: MetaFailure.nullable(),
  postedBy: z.string().min(1),
  postedByName: z.string(),
  postedAt: Instant.nullable(),
})
export type Reply = z.infer<typeof Reply>

export const PostReplyRequest = z.strictObject({
  text: z.string().trim().min(1).max(REPLY_TEXT_MAX_CHARS),
  suggestionId: Id.nullable(),
  inReplyToMessageId: Id.optional(),
})
export type PostReplyRequest = z.infer<typeof PostReplyRequest>
